import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../api/axiosInstance';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Switch,
    FormControl,
    FormLabel,
    Input,
    Button,
    Text,
    Flex,
    Select,
    SimpleGrid,
    IconButton,
    Tooltip,
    Image,
} from '@chakra-ui/react';
import { DeleteIcon } from '@chakra-ui/icons';
import { useNotification } from '../../../contexts/NotificationContext';

const FormEditor = () => {
    const { formId, productId } = useParams();
    const navigate = useNavigate();
    const [form, setForm] = useState({
        fields: [],
        description: '',
        shipping_label: '',
        shipping_field: '',
        shipping_value: '',
        upsells: [],
        terms_text: '',
        terms_link_text: '',
        terms_link_url: '',
        terms_checked_by_default: false,
    });
    const [products, setProducts] = useState([]);
    const { addNotification, addErrorNotifications } = useNotification();
    const fieldOptions = ['name', 'phone', 'county', 'city', 'address'];

    useEffect(() => {
        if (formId) {
            const fetchForm = async () => {
                const response = await axiosInstance.get(`/forms/${formId}`);
                const data = response.data || {};
                const upsells = Array.isArray(data.upsells)
                    ? data.upsells.map(u => ({
                        ...u,
                        quantity: (u.quantity ?? 1),
                    }))
                    : [];
                setForm({ ...data, upsells, terms_checked_by_default: !!data.terms_checked_by_default });
            };
            fetchForm();
        }

        const fetchProducts = async () => {
            const response = await axiosInstance.get(`/products/dictionary`);
            setProducts(response.data);
        };
        fetchProducts();
    }, [formId]);

    const handleFieldChange = (index, field, value) => {
        const newFields = [...form.fields];
        newFields[index][field] = value;
        setForm({ ...form, fields: newFields });
    };

    const handleUpsellChange = (index, field, value) => {
        const newUpsells = [...form.upsells];
        newUpsells[index][field] = value;
        setForm({ ...form, upsells: newUpsells });
    };

    const handleImageChange = (index, event) => {
        const file = event.target.files[0];

        const newUpsells = [...form.upsells];
        if (file) {
            newUpsells[index].image = file;
            newUpsells[index].image_url = URL.createObjectURL(file);
        } else {
            newUpsells[index].image = null;
            newUpsells[index].image_url = '';
        }

        setForm({ ...form, upsells: newUpsells });
    };

    const addField = () => {
        setForm({
            ...form,
            fields: [...form.fields, { name: '', label: '', description: '', type: 'text' }],
        });
    };

    const removeField = (index) => {
        const newFields = form.fields.filter((_, i) => i !== index);
        setForm({ ...form, fields: newFields });
    };

    const addUpsell = () => {
        setForm({
            ...form,
            upsells: [
                ...form.upsells,
                { product_id: '', value: '', image: null, image_url: '', full_text: '', quantity: 1 },
            ],
        });
    };

    const removeUpsell = (index) => {
        const newUpsells = form.upsells.filter((_, i) => i !== index);
        setForm({ ...form, upsells: newUpsells });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('description', form.description);
        formData.append('shipping_label', form.shipping_label);
        formData.append('shipping_field', form.shipping_field);
        formData.append('shipping_value', form.shipping_value);
        formData.append('product_id', productId);

        formData.append('terms_text', form.terms_text || '');
        formData.append('terms_link_text', form.terms_link_text || '');
        formData.append('terms_link_url', form.terms_link_url || '');
        formData.append('terms_checked_by_default', form.terms_checked_by_default ? '1' : '0');

        form.fields.forEach((field, index) => {
            formData.append(`fields[${index}][name]`, field.name);
            formData.append(`fields[${index}][label]`, field.label);
            formData.append(`fields[${index}][description]`, field.description);
            formData.append(`fields[${index}][type]`, field.type);
        });

        form.upsells.forEach((upsell, index) => {
            formData.append(`upsells[${index}][product_id]`, upsell.product_id);
            formData.append(`upsells[${index}][value]`, upsell.value);
            formData.append(`upsells[${index}][full_text]`, upsell.full_text);
            formData.append(`upsells[${index}][quantity]`, upsell.quantity ?? 1);

            if (upsell.image) {
                formData.append(`upsells[${index}][image]`, upsell.image);
            }

            if (upsell.image_url) {
                formData.append(`upsells[${index}][image_url]`, upsell.image_url);
            }
        });

        try {
            if (formId) {
                formData.append(`_method`, 'PUT');
                await axiosInstance.post(`/forms/${formId}`, formData);
                addNotification('Form updated successfully', 'success');
            } else {
                await axiosInstance.post('/forms', formData);
                addNotification('Form created successfully', 'success');
            }
            navigate('/admin/products');
        } catch (error) {
            addErrorNotifications(error.response?.data);
        }
    };

    const getAvailableOptions = (currentName) => {
        const selectedNames = form.fields.map((field) => field.name);
        return fieldOptions.filter((option) => !selectedNames.includes(option) || option === currentName);
    };

    return (
        <Box
            maxWidth="xxl"
            px={{ base: '20px', md: '40px' }}
            py={{ base: '130px', md: '80px' }}
            bg="white"
            boxShadow="lg"
            rounded="md"
        >
            <form onSubmit={handleSubmit}>
                <FormControl mb="4">
                    <FormLabel>Description</FormLabel>
                    <Input
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                    />
                </FormControl>
                <FormControl mb="4">
                    <FormLabel>Shipping Label</FormLabel>
                    <Input
                        value={form.shipping_label}
                        onChange={(e) => setForm({ ...form, shipping_label: e.target.value })}
                    />
                </FormControl>
                <FormControl mb="4">
                    <FormLabel>Shipping Field</FormLabel>
                    <Input
                        value={form.shipping_field}
                        onChange={(e) => setForm({ ...form, shipping_field: e.target.value })}
                    />
                </FormControl>
                <FormControl mb="4">
                    <FormLabel>Shipping Value</FormLabel>
                    <Input
                        type="number"
                        value={form.shipping_value}
                        onChange={(e) => setForm({ ...form, shipping_value: e.target.value })}
                    />
                </FormControl>

                <Box mb="8" p="4" bg="gray.50" rounded="md">
                    <Text fontSize="xl" fontWeight="bold" mb="4">
                        Terms and Conditions Settings (Optional)
                    </Text>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing="4">
                        <FormControl>
                            <FormLabel>General Text (e.g., "I agree to the")</FormLabel>
                            <Input
                                placeholder="Am citit si sunt de acord cu"
                                value={form.terms_text}
                                onChange={(e) => setForm({ ...form, terms_text: e.target.value })}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Link Text (The highlighted part)</FormLabel>
                            <Input
                                placeholder="Termenii si Conditiile"
                                value={form.terms_link_text}
                                onChange={(e) => setForm({ ...form, terms_link_text: e.target.value })}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Link URL</FormLabel>
                            <Input
                                placeholder="https://site.com/termeni"
                                value={form.terms_link_url}
                                onChange={(e) => setForm({ ...form, terms_link_url: e.target.value })}
                            />
                        </FormControl>

                        <FormControl display="flex" alignItems="center">
                            <FormLabel mb="0">Checked by default?</FormLabel>
                            <Switch
                                isChecked={form.terms_checked_by_default}
                                onChange={(e) => setForm({ ...form, terms_checked_by_default: e.target.checked })}
                            />
                        </FormControl>
                    </SimpleGrid>

                    <Box mt="4" p="2" border="1px dashed gray" rounded="sm" bg="white">
                        <Text fontSize="xs" color="gray.500">Preview:</Text>
                        <Flex align="center" gap="2">
                            <input type="checkbox" checked={form.terms_checked_by_default} readOnly />
                            <Text fontSize="sm">
                                {form.terms_text}{' '}
                                <span style={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer' }}>
                                    {form.terms_link_text}
                                </span>
                            </Text>
                        </Flex>
                    </Box>
                </Box>

                <Box mb="8">
                    <Text fontSize="xl" mb="4">
                        Fields
                    </Text>
                    {form.fields.map((field, index) => (
                        <SimpleGrid columns={{ base: 1, md: 5 }} spacing="4" mb="4" key={index}>
                            <FormControl>
                                <FormLabel>Name</FormLabel>
                                <Select
                                    value={field.name}
                                    onChange={(e) => handleFieldChange(index, 'name', e.target.value)}
                                    placeholder="Select a field"
                                >
                                    {getAvailableOptions(field.name).map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl>
                                <FormLabel>Label</FormLabel>
                                <Input
                                    value={field.label}
                                    onChange={(e) => handleFieldChange(index, 'label', e.target.value)}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Description</FormLabel>
                                <Input
                                    value={field.description}
                                    onChange={(e) => handleFieldChange(index, 'description', e.target.value)}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Type</FormLabel>
                                <Select
                                    value={field.type}
                                    onChange={(e) => handleFieldChange(index, 'type', e.target.value)}
                                >
                                    <option value="text">Text</option>
                                    <option value="tel">Number</option>
                                </Select>
                            </FormControl>
                            <Flex alignItems="center" justifyContent="center">
                                <Tooltip label="Delete Field">
                                    <IconButton
                                        icon={<DeleteIcon />}
                                        aria-label="delete"
                                        onClick={() => removeField(index)}
                                        colorScheme="red"
                                    />
                                </Tooltip>
                            </Flex>
                        </SimpleGrid>
                    ))}
                    <Button colorScheme="blue" onClick={addField}>
                        Add Field
                    </Button>
                </Box>

                <Box mb="8">
                    <Text fontSize="xl" mb="4">
                        Upsell Products
                    </Text>
                    {form.upsells.map((upsell, index) => (
                        <SimpleGrid columns={{ base: 1, md: 6 }} spacing="4" mb="4" key={index}>
                            <FormControl>
                                <FormLabel>Product</FormLabel>
                                <Select
                                    value={upsell.product_id}
                                    onChange={(e) => handleUpsellChange(index, 'product_id', e.target.value)}
                                    placeholder="Select product"
                                >
                                    {products.map((product) => (
                                        <option key={product.id} value={product.id}>{product.name}</option>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl>
                                <FormLabel>Price</FormLabel>
                                <Input
                                    type="number"
                                    value={upsell.value}
                                    onChange={(e) => handleUpsellChange(index, 'value', e.target.value)}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel>Quantity</FormLabel>
                                <Input
                                    type="number"
                                    min={1}
                                    step={1}
                                    value={upsell.quantity ?? 1}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        const next = Math.max(1, Number.isNaN(Number(v)) ? 1 : parseInt(v, 10));
                                        handleUpsellChange(index, 'quantity', next);
                                    }}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel>Full Text</FormLabel>
                                <Input
                                    value={upsell.full_text}
                                    onChange={(e) => handleUpsellChange(index, 'full_text', e.target.value)}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel>Image</FormLabel>
                                <Input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleImageChange(index, e)}
                                />
                                {upsell.image_url && (
                                    <Box position="relative" mt="4">
                                        <Image src={upsell.image_url} alt="option" boxSize="100px" objectFit="cover" rounded="md" />
                                        <IconButton
                                            icon={<DeleteIcon />}
                                            aria-label="delete image"
                                            colorScheme="red"
                                            position="absolute"
                                            top="0"
                                            right="0"
                                            onClick={() => handleImageChange(index, { target: { files: [null] } })}
                                        />
                                    </Box>
                                )}
                            </FormControl>

                            <Flex alignItems="center" justifyContent="center">
                                <Tooltip label="Delete Upsell">
                                    <IconButton
                                        icon={<DeleteIcon />}
                                        aria-label="delete upsell"
                                        onClick={() => removeUpsell(index)}
                                        colorScheme="red"
                                    />
                                </Tooltip>
                            </Flex>
                        </SimpleGrid>
                    ))}
                    <Button colorScheme="blue" onClick={addUpsell}>
                        Add Upsell
                    </Button>
                </Box>

                <Button type="submit" colorScheme="teal" mt="6">
                    Save
                </Button>
            </form>
        </Box>
    );
};

export default FormEditor;